/**
 * Top-level routes.
 *
 *   /            capture (home)
 *   /timeline    personal timeline
 *   /chain       community hue chain
 *   /account     sign in + account
 *   /about       what my daily hue is + how it works
 *   /privacy /terms /acceptable-use /cookies   legal
 *
 * All pages are lazy-loaded so the initial bundle stays small.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { ContactFeedbackModal } from './components/ContactFeedbackModal';
import { useAuth } from './lib/auth';
import { RequireReviewer } from './components/RequireReviewer';

const CapturePage = lazy(() => import('./pages/CapturePage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const ChainPage = lazy(() => import('./pages/ChainPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const MemorableHuePage = lazy(() => import('./pages/MemorableHuePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AcceptableUsePage = lazy(() => import('./pages/AcceptableUsePage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const McpSkillPage = lazy(() => import('./pages/McpSkillPage'));

function Loading() {
  return <div className="p-6 text-sm text-[color:var(--text-muted)]">Loading…</div>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

function Shell() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?contact=1 in the URL opens the contact modal globally. Lets the
  // native apps deep-link by opening mydailyhue.com/?contact=1 in the
  // browser, and lets the in-app nav link work via plain anchors.
  const contactOpen = searchParams.get('contact') === '1';
  const closeContact = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('contact');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <Nav />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<CapturePage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/chain" element={<ChainPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/memorable/:hueId" element={<MemorableHuePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/acceptable-use" element={<AcceptableUsePage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route
              path="/mcp/skill"
              element={
                <RequireReviewer>
                  <McpSkillPage />
                </RequireReviewer>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <ContactFeedbackModal
        isOpen={contactOpen}
        onClose={closeContact}
        context={location.pathname}
      />
    </div>
  );
}

/**
 * Site nav. Two layouts gated by the `sm:` breakpoint:
 *   - Wide (sm and up): inline links to the right of the wordmark.
 *   - Narrow (mobile, zoomed-in desktop): hamburger button that opens a
 *     full-width dropdown drawer. Avoids the cramped wrap we had where
 *     "privacy" and "terms" dropped onto a second tiny line.
 */
function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the drawer whenever the route changes so a click on a link
  // doesn't leave the drawer hanging open over the next page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <nav className="relative border-b border-[color:var(--border)]">
      <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
        <Link
          to="/"
          className="flex items-center gap-4 tracking-tight"
        >
          <img
            src="/icon-192.png"
            alt=""
            aria-hidden="true"
            width={64}
            height={64}
            className="h-14 w-14 shrink-0 rounded-full sm:h-16 sm:w-16"
          />
          <span className="font-serif text-3xl italic leading-none sm:text-4xl">
            my daily hue
          </span>
        </Link>

        {/* Inline links — wide viewports only */}
        <div className="hidden items-center gap-x-5 text-sm text-[color:var(--text-muted)] sm:flex">
          <NavLinks />
        </div>

        {/* Hamburger — narrow viewports only */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'close menu' : 'open menu'}
          aria-expanded={open}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[color:var(--text)] hover:bg-white/5 sm:hidden"
        >
          {/* Inline SVG so we don't pull in an icon lib just for this. */}
          {open ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          )}
        </button>
      </div>

      {/* Drawer */}
      {open && (
        <div className="absolute inset-x-0 top-full z-40 border-b border-[color:var(--border)] bg-[color:var(--bg)] sm:hidden">
          <div className="flex flex-col gap-1 px-5 py-3 text-sm text-[color:var(--text-muted)]">
            <NavLinks vertical />
          </div>
        </div>
      )}
    </nav>
  );
}

function NavLinks({ vertical = false }: { vertical?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isReviewer = user?.is_reviewer === 1;
  const linkBase = vertical
    ? 'py-2 hover:text-[color:var(--text)] text-left'
    : 'hover:text-[color:var(--text)]';
  const sep = vertical ? null : (
    <span className="text-white/10" aria-hidden>
      ·
    </span>
  );

  // Contact opens the modal globally by setting ?contact=1 on the
  // current path. Native apps and external links can do the same by
  // visiting mydailyhue.com/?contact=1 directly.
  const openContact = () => {
    const params = new URLSearchParams(location.search);
    params.set('contact', '1');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  return (
    <>
      <Link to="/timeline" className={linkBase}>
        timeline
      </Link>
      <Link to="/chain" className={linkBase}>
        chain
      </Link>
      <Link to="/account" className={linkBase}>
        account
      </Link>
      {isReviewer && (
        <Link to="/mcp/skill" className={linkBase}>
          mcp skill
        </Link>
      )}
      <button type="button" onClick={openContact} className={linkBase}>
        contact
      </button>
      {sep}
      <Link to="/about" className={vertical ? linkBase : `text-xs ${linkBase}`}>
        about
      </Link>
      <Link to="/privacy" className={vertical ? linkBase : `text-xs ${linkBase}`}>
        privacy
      </Link>
      <Link to="/terms" className={vertical ? linkBase : `text-xs ${linkBase}`}>
        terms
      </Link>
    </>
  );
}

function NotFound() {
  return <div className="p-6">not found</div>;
}
